/*
  Article — chapitres repliables, sommaire ancre, barre d'appel.

  Le gabarit rend TOUS les chapitres ouverts. C'est volontaire : sans
  JavaScript, l'article reste un article complet, et le moteur voit un texte
  continu. Ce fichier ne fait que replier, sur mobile uniquement, ce qui rend
  la page interminable au pouce.

  Replier apres le rendu ne coute pas de CLS : les chapitres replies sont sous
  la ligne de flottaison, et rien de visible ne bouge.
*/

const REQUETE_MOBILE = '(max-width: 749px)';

class ChapitresArticle extends HTMLElement {
  connectedCallback() {
    this.chapitres = Array.from(this.querySelectorAll('.article-page__chapitre'));
    if (this.chapitres.length === 0) return;

    this.liens = Array.from(this.querySelectorAll('[data-sommaire] a[href^="#"]'));
    this.boutonDeplier = this.querySelector('[data-deplier]');
    this.mobile = window.matchMedia(REQUETE_MOBILE);
    this.doux = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.appliquerFormat = this.appliquerFormat.bind(this);
    this.surAncre = this.surAncre.bind(this);
    this.majBouton = this.majBouton.bind(this);

    this.mobile.addEventListener('change', this.appliquerFormat);
    this.liens.forEach((lien) => lien.addEventListener('click', this.surAncre));

    // `toggle` ne remonte pas : on l'ecoute en phase de capture.
    this.addEventListener('toggle', this.majBouton, true);

    if (this.boutonDeplier) {
      // Le bouton n'a aucun sens sans script : il n'apparait qu'ici.
      this.boutonDeplier.hidden = false;
      this.boutonDeplier.addEventListener('click', () => {
        const ouvrir = this.chapitres.some((chapitre) => !chapitre.open);
        this.chapitres.forEach((chapitre) => {
          chapitre.open = ouvrir;
        });
      });
    }

    this.appliquerFormat();
    window.addEventListener('hashchange', () => this.ouvrirCible(window.location.hash));
    if (window.location.hash) this.ouvrirCible(window.location.hash);

    this.suivreLecture();
    this.habillerTableaux();
  }

  disconnectedCallback() {
    this.mobile.removeEventListener('change', this.appliquerFormat);
    this.observateurChapitres?.disconnect();
    if (this.surDefilement) {
      window.removeEventListener('scroll', this.surDefilement);
      window.removeEventListener('resize', this.surDefilement);
      this.large?.removeEventListener('change', this.surDefilement);
    }
  }

  /* Chapitre courant dans le sommaire, et jauge de lecture.

     Les deux disent la meme chose sous deux formes : ou j'en suis. Le
     sommaire le dit precisement (quel chapitre), la jauge approximativement
     (combien il reste). Aucun des deux n'est annonce au lecteur d'ecran :
     `aria-current` suffit sur le lien, et la jauge est `aria-hidden`. */
  suivreLecture() {
    this.jauge = this.querySelector('[data-jauge]');
    this.barre = this.querySelector('[data-jauge] .article-page__jauge-barre');
    this.corps = this.querySelector('[data-corps]');

    if (this.jauge && this.barre && this.corps) {
      // La jauge n'existe qu'avec le rail, donc au-dela de 1200 px. Sans ce
      // garde-fou, le telephone lirait la position du corps a chaque
      // defilement pour alimenter un element que la feuille masque.
      // Valeur a tenir alignee sur le point de rupture de section-article.css.
      this.large = window.matchMedia('(min-width: 1200px)');
      this.jauge.hidden = false;
      this.enAttente = false;
      this.surDefilement = () => {
        if (this.enAttente || !this.large.matches) return;
        this.enAttente = true;
        window.requestAnimationFrame(() => this.majJauge());
      };
      window.addEventListener('scroll', this.surDefilement, { passive: true });
      window.addEventListener('resize', this.surDefilement, { passive: true });
      this.large.addEventListener('change', this.surDefilement);
      this.surDefilement();
    }

    if (this.liens.length === 0 || !('IntersectionObserver' in window)) return;

    // Le titre du chapitre courant doit avoir passe sous l'en-tete fixe, pas
    // sous le haut de la fenetre : sinon le sommaire designe un chapitre que
    // la barre recouvre encore.
    //
    // --hauteur-entete est un calc() de trois autres jetons. getPropertyValue
    // rendrait la chaine "calc(...)", pas une valeur en pixels : on ne peut
    // pas la lire, il faut la faire resoudre. D'ou la sonde, mesuree puis
    // retiree — une seule lecture de mise en page, a l'initialisation.
    const sonde = document.createElement('div');
    sonde.style.cssText =
      'position:absolute;top:0;left:0;visibility:hidden;pointer-events:none;height:var(--hauteur-entete)';
    document.body.appendChild(sonde);
    const marge = sonde.offsetHeight || 140;
    sonde.remove();

    this.visibles = new Set();
    this.observateurChapitres = new IntersectionObserver(
      (entrees) => {
        entrees.forEach((entree) => {
          if (entree.isIntersecting) this.visibles.add(entree.target.id);
          else this.visibles.delete(entree.target.id);
        });
        this.majChapitreCourant();
      },
      { rootMargin: `-${marge + 8}px 0px -55% 0px`, threshold: 0 }
    );

    this.chapitres.forEach((chapitre) => this.observateurChapitres.observe(chapitre));
  }

  majChapitreCourant() {
    // Plusieurs chapitres peuvent etre dans la fenetre a la fois : on retient
    // le premier dans l'ordre du document, celui qu'on est en train de lire.
    const courant = this.chapitres.find((chapitre) => this.visibles.has(chapitre.id));
    if (!courant && this.courant === undefined) return;
    if (courant && courant.id === this.courant) return;
    this.courant = courant ? courant.id : null;

    this.liens.forEach((lien) => {
      if (this.courant && lien.getAttribute('href') === `#${this.courant}`) {
        lien.setAttribute('aria-current', 'true');
      } else {
        lien.removeAttribute('aria-current');
      }
    });
  }

  majJauge() {
    this.enAttente = false;
    const cadre = this.corps.getBoundingClientRect();
    const course = cadre.height - window.innerHeight;
    // Un article plus court que l'ecran n'a pas de progression a montrer.
    if (course <= 0) {
      this.jauge.hidden = true;
      return;
    }
    this.jauge.hidden = false;
    const part = Math.min(1, Math.max(0, -cadre.top / course));
    this.barre.style.transform = `scaleX(${part})`;
  }

  /* Les tableaux saisis dans l'admin n'ont aucune enveloppe. Sans elle, un
     tableau matieres a cinq colonnes deborde de la mesure de lecture sur
     telephone. On lui en donne une, focalisable pour rester atteignable au
     clavier (WCAG 2.1.1) — un conteneur qui defile mais qu'on ne peut pas
     atteindre sans souris est une impasse. */
  habillerTableaux() {
    const tableaux = this.querySelectorAll('.article-page__contenu table');

    tableaux.forEach((tableau) => {
      if (tableau.parentElement.classList.contains('article-page__tableau')) return;

      const cadre = document.createElement('div');
      cadre.className = 'article-page__tableau';
      cadre.tabIndex = 0;

      // Un role region sans nom accessible est pire que pas de role du tout :
      // il n'est annonce que si le tableau porte une legende.
      const legende = tableau.querySelector('caption');
      if (legende && legende.textContent.trim()) {
        cadre.setAttribute('role', 'region');
        cadre.setAttribute('aria-label', legende.textContent.trim());
      }

      tableau.parentNode.insertBefore(cadre, tableau);
      cadre.appendChild(tableau);
    });
  }

  /* Mobile : un seul chapitre ouvert, le premier, pour que le sommaire soit
     atteignable en deux gestes. Ordinateur : tout ouvert, la hauteur de page
     n'y est pas un probleme. */
  appliquerFormat() {
    this.chapitres.forEach((chapitre, rang) => {
      chapitre.open = this.mobile.matches ? rang === 0 : true;
    });
    this.majBouton();
  }

  majBouton() {
    if (!this.boutonDeplier) return;
    const tousOuverts = this.chapitres.every((chapitre) => chapitre.open);
    this.boutonDeplier.textContent = tousOuverts
      ? this.boutonDeplier.dataset.libelleFermer
      : this.boutonDeplier.dataset.libelleOuvrir;
    this.boutonDeplier.setAttribute('aria-expanded', String(tousOuverts));
  }

  /* Un lien du sommaire vers un chapitre replie n'amenerait que sur son titre.
     On l'ouvre avant de faire defiler, sinon le lecteur atterrit sur une ligne
     fermee et croit que le lien est casse. */
  surAncre(evenement) {
    const cible = this.ouvrirCible(evenement.currentTarget.getAttribute('href'));
    if (!cible) return;

    evenement.preventDefault();
    cible.scrollIntoView({ behavior: this.doux ? 'smooth' : 'auto', block: 'start' });
    // L'URL garde l'ancre : le lien reste partageable et le retour arriere marche.
    window.history.replaceState(null, '', evenement.currentTarget.getAttribute('href'));

    const titre = cible.querySelector('summary');
    if (titre) titre.focus({ preventScroll: true });
  }

  ouvrirCible(ancre) {
    if (!ancre || ancre.length < 2) return null;
    const cible = this.querySelector(`#${CSS.escape(ancre.slice(1))}`);
    if (!cible) return null;
    if (cible.tagName === 'DETAILS') cible.open = true;
    return cible;
  }
}

customElements.define('chapitres-article', ChapitresArticle);

/*
  Barre d'appel de fin de lecture.

  Meme convention que la barre d'achat de la fiche produit : fixee en bas,
  masquee par defaut, revelee quand le lecteur est entre dans le corps de
  l'article, et retiree des que la carte produit de fin est a l'ecran — les
  deux ne se disputent jamais la place.
*/
class BarreArticle extends HTMLElement {
  connectedCallback() {
    this.barre = this.querySelector('[data-barre]');
    if (!this.barre) return;

    const depart = document.querySelector('.article-page__contenu');
    const carte = document.querySelector('.article-page__rappel');
    if (!depart || !('IntersectionObserver' in window)) return;

    this.lue = false;
    this.arrivee = false;

    this.observateurDepart = new IntersectionObserver(
      ([entree]) => {
        // `boundingClientRect.top < 0` : le corps du texte est passe au-dessus
        // de l'ecran, donc la lecture a commence.
        this.lue = !entree.isIntersecting && entree.boundingClientRect.top < 0;
        this.majVisibilite();
      },
      { threshold: 0 }
    );
    this.observateurDepart.observe(depart);

    if (carte) {
      this.observateurArrivee = new IntersectionObserver(
        ([entree]) => {
          this.arrivee = entree.isIntersecting;
          this.majVisibilite();
        },
        { threshold: 0 }
      );
      this.observateurArrivee.observe(carte);
    }
  }

  disconnectedCallback() {
    this.observateurDepart?.disconnect();
    this.observateurArrivee?.disconnect();
  }

  majVisibilite() {
    this.barre.hidden = !this.lue || this.arrivee;
  }
}

customElements.define('barre-article', BarreArticle);
